export interface PHITestCase {
  id: string;
  name: string;
  prompt: string;
  description: string;
  expectedDetections: string[];
  severity: string;
}

export const phiTestCases: PHITestCase[] = [
  {
    id: 'basic-name',
    name: 'Basic Name Detection',
    prompt: 'Patient John Smith was admitted today.',
    description: 'Tests detection of patient names',
    expectedDetections: ['names'],
    severity: 'medium'
  },
  {
    id: 'complex-medical-scenario',
    name: 'Complex Medical Scenario',
    prompt:
      "Dr. Martinez needs to review the case of Maria Elena Rodriguez, a 45-year-old female patient (MRN: HOS-2024-789456, SSN: 123-45-6789) who was admitted on March 15, 2024, with chest pain. Her emergency contact is her husband Carlos at (555) 234-5678. Please send the discharge summary to maria.rodriguez@email.com and schedule a follow-up at our downtown clinic located at 456 Medical Center Drive, Suite 200, Springfield, IL 62701. The patient's insurance ID is BC-789-456-123.",
    description: 'Comprehensive test with multiple PHI types in a realistic medical scenario',
    expectedDetections: [
      'names',
      'dates',
      'mrn',
      'ssn',
      'phone',
      'email',
      'geographic',
      'unique_id'
    ],
    severity: 'critical'
  },
  {
    id: 'ssn-detection',
    name: 'SSN Detection',
    prompt: 'The patient with SSN 123-45-6789 needs immediate attention.',
    description: 'Tests detection of Social Security Numbers',
    expectedDetections: ['ssn'],
    severity: 'high'
  },
  {
    id: 'emergency-room-scenario',
    name: 'Emergency Room Scenario',
    prompt:
      "URGENT: Patient Jennifer Walsh (DOB: 08/22/1987) arrived via ambulance at 14:30 hours. Vitals: BP 180/110, HR 120. Patient reports severe headache starting at 12:00 PM. Emergency contact: Michael Walsh at 555-789-0123. Patient ID bracelet shows MRN: ER-2024-001234. Please notify Dr. Sarah Chen immediately at extension 4567. Patient's home address is 789 Oak Street, Apartment 3B, Chicago, IL 60601.",
    description: 'High-pressure emergency scenario with time-sensitive PHI',
    expectedDetections: ['names', 'dates', 'phone', 'mrn', 'geographic'],
    severity: 'critical'
  },
  {
    id: 'date-detection',
    name: 'Date Detection',
    prompt: 'Patient was born on 01/15/1980 and admitted on 03/22/2024.',
    description: 'Tests detection of birth dates and admission dates',
    expectedDetections: ['dates'],
    severity: 'medium'
  },
  {
    id: 'psychiatric-consultation',
    name: 'Psychiatric Consultation',
    prompt:
      'Patient Robert Thompson (age 34, SSN: 987-65-4321) is scheduled for psychiatric evaluation on April 10, 2024. History includes depression and anxiety. Previous therapist was Dr. Lisa Park, contact: lisa.park@mentalhealth.org, phone (555) 456-7890. Patient currently resides at Sunrise Assisted Living, 321 Wellness Boulevard, Room 15A, Portland, OR 97201. Insurance: Medicaid ID MED-OR-789456123.',
    description: 'Sensitive psychiatric case with multiple identifiers and contacts',
    expectedDetections: ['names', 'ssn', 'dates', 'email', 'phone', 'geographic', 'unique_id'],
    severity: 'critical'
  },
  {
    id: 'phone-detection',
    name: 'Phone Number Detection',
    prompt: 'Please call the patient at (555) 123-4567 for follow-up.',
    description: 'Tests detection of phone numbers',
    expectedDetections: ['phone'],
    severity: 'medium'
  },
  {
    id: 'pediatric-case',
    name: 'Pediatric Case',
    prompt:
      "Minor patient Emma Johnson (DOB: 12/05/2015, age 8) accompanied by guardian Lisa Johnson (SSN: 456-78-9012). Child's school nurse reported incident at Lincoln Elementary School, 123 School Street, Riverside, CA 92501. Guardian can be reached at (555) 321-9876 or ljohnson@email.com. Pediatrician Dr. Amanda Foster has been notified. Patient MRN: PED-2024-567890.",
    description: 'Pediatric case involving minor patient and guardian information',
    expectedDetections: ['names', 'dates', 'ssn', 'phone', 'email', 'geographic', 'mrn'],
    severity: 'critical'
  },
  {
    id: 'email-detection',
    name: 'Email Detection',
    prompt: 'Send the results to patient.email@hospital.com',
    description: 'Tests detection of email addresses',
    expectedDetections: ['email'],
    severity: 'medium'
  },
  {
    id: 'surgical-procedure',
    name: 'Surgical Procedure',
    prompt:
      "Pre-operative clearance for patient David Kim (MRN: SUR-2024-445566, DOB: 03/18/1965) scheduled for cardiac bypass surgery on May 2, 2024, at 07:00 AM. Surgeon: Dr. Michael Rodriguez. Anesthesiologist: Dr. Sarah Williams. Patient's wife Helen Kim (emergency contact: 555-667-8899) will be in waiting room. Post-op recovery room assignment: ICU-12. Patient allergic to penicillin (documented 04/15/2023). Insurance pre-auth: AUTH-789456123.",
    description: 'Complex surgical case with multiple healthcare providers and scheduling details',
    expectedDetections: ['names', 'mrn', 'dates', 'phone', 'unique_id'],
    severity: 'critical'
  },
  {
    id: 'mrn-detection',
    name: 'Medical Record Number',
    prompt: 'Patient MRN 987654321 has completed the procedure.',
    description: 'Tests detection of medical record numbers',
    expectedDetections: ['mrn'],
    severity: 'high'
  },
  {
    id: 'lab-results-scenario',
    name: 'Lab Results Scenario',
    prompt:
      'Laboratory results for patient Amanda Foster (SSN: 234-56-7890, DOB: 07/30/1982) are ready for review. Blood work collected on 04/20/2024 at 08:15 AM shows elevated glucose levels. Please contact patient at amanda.foster@email.com or (555) 890-1234 to schedule follow-up appointment with endocrinologist Dr. James Wilson. Patient address: 567 Maple Avenue, Unit 4C, Denver, CO 80202. Lab order #: LAB-2024-998877.',
    description: 'Laboratory results with patient contact and scheduling information',
    expectedDetections: ['names', 'ssn', 'dates', 'email', 'phone', 'geographic', 'unique_id'],
    severity: 'high'
  },
  {
    id: 'multiple-phi',
    name: 'Multiple PHI Types',
    prompt:
      'Patient Sarah Johnson (DOB: 05/12/1975, SSN: 456-78-9012, Phone: 555-987-6543) requires urgent care.',
    description: 'Tests detection of multiple PHI types in one prompt',
    expectedDetections: ['names', 'dates', 'ssn', 'phone'],
    severity: 'critical'
  },
  {
    id: 'insurance-billing',
    name: 'Insurance & Billing',
    prompt:
      'Billing inquiry for patient Thomas Anderson (Policy #: INS-BC-789456, Group #: EMP-12345, SSN: 345-67-8901). Patient DOB: 11/25/1978. Claim submitted for office visit on 03/28/2024. Provider: Dr. Nancy White, NPI: 1234567890. Patient employer: Tech Solutions Inc., 890 Business Park Drive, Austin, TX 78701. Contact billing department at (555) 234-5678 ext. 101 or billing@healthcorp.com.',
    description: 'Complex billing scenario with insurance and employer information',
    expectedDetections: ['names', 'unique_id', 'ssn', 'dates', 'phone', 'email', 'geographic'],
    severity: 'high'
  },
  {
    id: 'address-detection',
    name: 'Address Detection',
    prompt: 'Patient lives at 123 Main Street, Anytown, CA 90210.',
    description: 'Tests detection of geographic locations',
    expectedDetections: ['geographic'],
    severity: 'medium'
  },
  {
    id: 'telemedicine-session',
    name: 'Telemedicine Session',
    prompt:
      'Telemedicine consultation scheduled for patient Michelle Davis (MRN: TELE-2024-334455) on April 25, 2024, at 2:00 PM PST. Patient will join from home IP address 192.168.1.45. Backup contact: spouse John Davis at (555) 445-6677. Session ID: ZOOM-789-456-123. Patient email: m.davis@email.com. Technical support: Call IT helpdesk at (555) 123-HELP. Patient located in rural area: 1234 Country Road 15, Farmington, NM 87401.',
    description: 'Modern telemedicine scenario with digital identifiers and remote access',
    expectedDetections: [
      'names',
      'mrn',
      'dates',
      'ip_address',
      'phone',
      'unique_id',
      'email',
      'geographic'
    ],
    severity: 'critical'
  },
  {
    id: 'clean-prompt',
    name: 'Clean Medical Query',
    prompt:
      'What are the standard treatment protocols for hypertension in elderly patients? Please provide evidence-based guidelines for medication management and lifestyle interventions.',
    description: 'Tests that clean medical queries are not flagged',
    expectedDetections: [],
    severity: 'low'
  },
  {
    id: 'research-scenario',
    name: 'Medical Research Scenario',
    prompt:
      'Research study participant #RSH-2024-789 (anonymized) shows promising results in clinical trial NCT04567890. Study coordinator Dr. Patricia Lee can be reached at p.lee@research.edu or (555) 789-0123. IRB approval #: IRB-2024-001. Data collection site: University Medical Center, 456 Research Drive, Boston, MA 02115. Next data collection: May 15, 2024.',
    description: 'Research context with anonymized data but some identifiable information',
    expectedDetections: ['names', 'email', 'phone', 'unique_id', 'geographic', 'dates'],
    severity: 'medium'
  },
  {
    id: 'ip-address',
    name: 'IP Address Detection',
    prompt: 'The patient accessed the portal from IP address 192.168.1.100.',
    description: 'Tests detection of IP addresses',
    expectedDetections: ['ip_address'],
    severity: 'medium'
  },
  {
    id: 'pharmacy-prescription',
    name: 'Pharmacy Prescription',
    prompt:
      'Prescription for patient Catherine Wilson (DOB: 09/14/1990, Phone: 555-334-5566) ready for pickup at Downtown Pharmacy, 789 Central Avenue, Miami, FL 33101. Rx #: RX-2024-887766. Prescribed by Dr. Kevin Brown on 04/18/2024. Insurance copay: $25.00. Pharmacy contact: (555) PHARMA-1. Patient allergies: Sulfa drugs (noted 02/10/2023).',
    description: 'Pharmacy scenario with prescription and pickup details',
    expectedDetections: ['names', 'dates', 'phone', 'geographic', 'unique_id'],
    severity: 'high'
  },
  {
    id: 'medical-device-alert',
    name: 'Medical Device Alert',
    prompt:
      'DEVICE ALERT: Pacemaker patient alert for Robert Chen (Device ID: PM-2024-556677, SSN: 567-89-0123, DOB: 01/08/1955). Device manufactured by CardioTech Inc., Serial #: CT-789456123. Implanted on 02/20/2024 by Dr. Lisa Martinez at Heart Center, 321 Cardiac Way, Seattle, WA 98101. Patient contact: (555) 678-9012, r.chen@email.com. Emergency contact: spouse Mary Chen (555) 678-9013.',
    description: 'Medical device monitoring with device identifiers and patient information',
    expectedDetections: ['names', 'unique_id', 'ssn', 'dates', 'phone', 'email', 'geographic'],
    severity: 'critical'
  },
  {
    id: 'clean-clinical-query',
    name: 'Clean Clinical Query',
    prompt:
      'What are the latest clinical guidelines for managing diabetes mellitus type 2? Please include information about HbA1c targets, medication algorithms, and monitoring protocols.',
    description: 'Professional medical query without PHI',
    expectedDetections: [],
    severity: 'low'
  }
];

export const getTestCasesByCategory = (category: string): PHITestCase[] => {
  return phiTestCases.filter((tc) => tc.expectedDetections.includes(category));
};

export const getPositiveTestCases = (): PHITestCase[] => {
  return phiTestCases.filter((tc) => tc.expectedDetections.length > 0);
};

export const getNegativeTestCases = (): PHITestCase[] => {
  return phiTestCases.filter((tc) => tc.expectedDetections.length === 0);
};
